import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Chip, chipClasses, ChipProps, darken, lighten, styled } from '@mui/material';
import classNames from 'classnames';

import { TitleType } from '@models/Title';

import { Icon, IconOption } from '../Icon';

import { TitleColorMap } from './utils';

export type TitleTypeChipProps = Omit<ChipProps, 'color' | 'icon' | 'label'> & {
  discrete?: boolean;
  titleType?: TitleType;
};

const TitleIconMap: Record<TitleType, IconOption> = {
  episode: 'playBox',
  movie: 'movie',
  program: 'televisionClassic',
  series: 'playBoxMultiple',
};

const StyledChip = styled(
  ({ titleType, ...props }: ChipProps & Pick<TitleTypeChipProps, 'titleType'>) => (
    <Chip {...props} />
  )
)`
  border-radius: 2px;
  background: ${({ titleType, theme }) =>
    alpha(theme.palette.custom.content[TitleColorMap[titleType as TitleType]], 0.2)};
  color: ${({ titleType, theme }) =>
    theme.palette.mode === 'dark'
      ? lighten(theme.palette.custom.content[TitleColorMap[titleType as TitleType]], 0.3)
      : darken(theme.palette.custom.content[TitleColorMap[titleType as TitleType]], 0.3)};

  &.${chipClasses.outlined} {
    border-color: ${({ titleType, theme }) =>
      alpha(theme.palette.custom.content[TitleColorMap[titleType as TitleType]], 0.25)};
  }

  .${chipClasses.icon} {
    font-size: 1rem;
    margin-left: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const TitleTypeChip: FC<TitleTypeChipProps> = ({
  className,
  discrete,
  size = 'small',
  titleType: titleType,
  variant = 'outlined',
  ...rest
}) => {
  const { t } = useTranslation();

  return titleType ? (
    <StyledChip
      className={classNames(className, { discrete })}
      icon={<Icon color='inherit' name={TitleIconMap[titleType]} />}
      label={t('titleType', { context: titleType })}
      size={size}
      titleType={titleType}
      variant={variant}
      {...rest}
    />
  ) : null;
};
